import React, { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { Button, Grid, Icon, MUIDataTable, MUIDataTableColumnDef } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useParams } from 'react-router-dom';
import EditSubjectModal from './EditSubjectModal';
import DeleteSubjectModal from './DeleteSubjectModal';
import { SpecialConsiderationSubjectModel } from '../../../api/candidate/special-consideration/model';

const useStyles = makeStyles()((theme) =>
({
  examLevelTable: {
    paddingBottom: theme.spacing(3),
  },
  buttonContainer: {
    display: 'inline-flex',
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  buttonRemove: {
    color: theme.color.state.stateError,
  },
  button: {
    margin: 'auto',
    paddingTop: 0,
    padding: 10,
    minWidth: 0,
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
  },
}));
const tableColumns = (
  data: SpecialConsiderationSubjectModel[],
  t: TFunction<'translation'>,
  classes: Record<string,string>,
  theme: any,
  amendment: any,
  onClickButtonEdit: any,
  onClickButtonDelete: any
): MUIDataTableColumnDef[] => [
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.ExamLevel'),
      name: 'examLevel',
      options: {
        setCellProps: () => ({
          style: {
            width: '13%',
          },
        }),
      },
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.Exam Series'),
      name: 'examSeries',
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.ModeOfAssessment'),
      name: 'modeOfAssessment',
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.SubjectPaper'),
      name: 'subject',
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.Special Considerations Type'),
      name: 'specialConsiderationType',
    },
    {
      name: 'reason',
      label: t('specialConsideration->>ApplyForSpecialConsideration.Reason'),
    },
    amendment
      ? ''
      : {
        name: 'action',
        label: t('specialConsideration->>ApplyForSpecialConsideration.Action'),
        options: {
          sort: false,
          setCellProps: () => ({
            style: { width: '15%' },
          }),
          customBodyRenderLite: (dataIndex: number) => (
            <Grid container>
              <Grid container spacing={2}>
                <Grid className={classes.buttonContainer}>
                  <Button
                      startIcon={<Icon size="M" name={IconName.edit} color={theme.color.icon.iconSelected} />}
                    id="button-accessArrangement-edit"
                    className={`${classes.button} ${classes.buttonFont}`}
                    onClick={() => onClickButtonEdit(data[dataIndex])}
                    variant="text"
                    color="primary"
                    size="small"
                  >
                    {t('accessArrangement->>AccessArrangementRequest.Button.Edit')}
                  </Button>
                </Grid>
                <Grid className={classes.buttonContainer}>
                  <Button
                      startIcon={<Icon size="M" name={IconName.trash} color={theme.color.icon.iconTrash} />}
                    id="button-accessArrangement-edit"
                    className={`${classes.button} ${classes.buttonFont} ${classes.buttonRemove}`}
                    onClick={() => onClickButtonDelete(data[dataIndex])}
                    variant="text"
                    size="small"
                  >
                    {t('components->>IconLabel.Remove')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          ),
        },
      },
  ];

export interface SubjectTableProps {
  data: SpecialConsiderationSubjectModel[];
  isFetching: boolean;
  refetchSubjectTable: () => void;
  amendment: boolean;
  setSubjectError?: (value: string | null) => void;
}

const SelectSubjectTable = ({
  data,
  isFetching,
  refetchSubjectTable,
  amendment,
  setSubjectError,
}: SubjectTableProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();
  const { uuid: requestUuid } = useParams<{ uuid: string }>();

  const [editModalState, setEditModalState] = useState<boolean>(false);
  const [deleteModalState, setDeleteModalState] = useState<boolean>(false);
  const [subjectName, setSubjectName] = useState<string>('');
  const [detailUuid, setDetailUuid] = useState<string>('');
  const [subjectSelected, setSubjectSelected] = useState<SpecialConsiderationSubjectModel>({
    uuid: '',
    examLevel: '',
    examSeries: '',
    modeOfAssessment: '',
    reason: '',
    specialConsiderationType: '',
    subject: '',
    examDate: '',
    explanation: '',
    paperUuid: '',
    draft: false,
    examYear: 0,
    reasonCode: '',
    specialConsiderationTypeCode: '',
    subjectUuid: '',
  });

  const onClickButtonDelete = (rowData: SpecialConsiderationSubjectModel) => {
    setSubjectName(rowData.subject);
    setDetailUuid(rowData.uuid);
    setDeleteModalState(true);
  };

  const onClickButtonEdit = (rowData: SpecialConsiderationSubjectModel) => {
    setSubjectSelected(rowData);
    setEditModalState(true);
  };

  return (
    <Grid sm={12} className={classes.examLevelTable}>
      <MUIDataTable
        isLoading={isFetching}
        columns={tableColumns(
          data,
          t,
          classes,
          theme,
          amendment,
          onClickButtonEdit,
          onClickButtonDelete
        )}
        data={data || []}
        emptyDataLabel={t(
          'specialConsideration->>ApplyForSpecialConsideration.NoSubjectPaperAdded'
        )}
      />

      <EditSubjectModal
        modalState={editModalState}
        onDialogClose={() => setEditModalState(false)}
        subjectSelected={subjectSelected}
        refetchSubjectTable={refetchSubjectTable}
      />
      <DeleteSubjectModal
        requestUuid={requestUuid}
        detailUuid={detailUuid}
        subjectName={subjectName}
        modalState={deleteModalState}
        onDialogClose={() => setDeleteModalState(false)}
        refetch={refetchSubjectTable}
        setSubjectError={setSubjectError}
      />
    </Grid>
  );
};

export default SelectSubjectTable;
